import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PersonButton } from '@/features/people/PersonDrawer';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Camera, CheckCircle2, ClipboardSignature, Image as ImageIcon, X } from 'lucide-react';
import { FORM_SCOPE_LABELS, validateSectionAnswers, type FormSection, type FormValues, type Handover, type HandoverDetail, type HandoverInput } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCanAnywhere } from '@/lib/permissions';
import { DATE_FORMATS, dt, fullDateTime } from '@/lib/format';
import clsx from 'clsx';
import { handoverAnswerGroups, handoverTitle } from './handoverAnswers';
export { useHandoverTemplatesAvailable } from './available';
import { handoverTemplateLink } from './available';
import { useTeams, useWorkspaceId } from '@/lib/hooks';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { discardUpload, uploadFile } from '@/lib/upload';
import { DialogFooter, Button, Field, Modal, Select, Spinner, Textarea } from '@/components/ui';
import { useWfLookups } from '../common';
import { SectionedFormInput, sectionDefaults } from '@/features/forms/FormBuilder';
import { RelativeTime } from '@/components/time/RelativeTime';

/** Structured handover sections, in display order (backlog #19). */
export const SECTIONS = [
  { key: 'issues', label: 'Issues', hint: 'Equipment faults, things left unfinished' },
  { key: 'stock', label: 'Stock', hint: 'Low stock, deliveries, returns waiting' },
  { key: 'security', label: 'Security', hint: 'Doors, alarms, keys, cash' },
  { key: 'incidents', label: 'Incidents', hint: 'Accidents, near misses, customer issues' },
  { key: 'notes', label: 'Notes', hint: 'Anything else the next shift should know' },
] as const;
type SectionKey = typeof SECTIONS[number]['key'];

export const handoverKeys = (ws: string) => qk.wf(ws, 'handovers');

function invalidate(ws: string | null) { void queryClient.invalidateQueries({ queryKey: handoverKeys(ws ?? '') }); }

export function HandoverPhoto({ handoverId, attachmentId, small }: { handoverId: string; attachmentId: string; small?: boolean }) {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: [...handoverKeys(ws ?? ''), 'photo', handoverId, attachmentId], queryFn: () => api.wf.handoverPhoto(ws!, handoverId, attachmentId), enabled: Boolean(ws), staleTime: 240_000 });
  const size = small ? 'h-12 w-12 rounded-md' : 'h-20 w-20 rounded-lg';
  if (!q.data) return <div className={`flex ${size} items-center justify-center bg-slate-100 dark:bg-slate-800`} data-testid="handover-photo"><ImageIcon className="h-4 w-4 text-slate-500" /></div>;
  return <a href={q.data.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} data-testid="handover-photo"><img src={q.data.url} alt={q.data.file_name} className={`${size} object-cover ring-1 ring-slate-200 dark:ring-slate-700`} /></a>;
}

/**
 * t275u: a handover's answers as a compact definition list - the question muted on the left, the answer on the right
 * (stacked on a phone); long text spans the full width underneath; checks read Yes / No; exceptions are semibold with
 * a small dot; empty answers are left out; photos are small thumbnails.
 */
export function HandoverAnswers({ h, small }: { h: Handover; small?: boolean }) {
  const groups = handoverAnswerGroups(h);
  if (!groups.length) return <p className="text-sm text-slate-500">No answers.</p>;
  return (
    <div className="space-y-3" data-testid="handover-answers">
      {groups.map(g => (
        <div key={g.id}>
          {g.title && <h3 className="mb-1 text-xs font-semibold text-slate-700 dark:text-slate-300">{g.title}</h3>}
          <dl className="grid grid-cols-1 gap-x-6 text-sm sm:gap-y-1.5 sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
            {g.rows.map(r => (
              <div key={r.id} className={r.wide ? 'sm:col-span-2' : 'contents'} data-testid="handover-answer" data-flag={r.flag || undefined} data-wide={r.wide || undefined}>
                <dt className="text-slate-500 dark:text-slate-400">{r.label}</dt>
                <dd className={clsx('min-w-0 break-words text-slate-900 dark:text-slate-100', r.wide ? 'mb-2 mt-0.5 whitespace-pre-wrap sm:mb-0' : 'mb-2 sm:mb-0', r.flag && 'font-semibold')}>
                  {r.files ? <div className="flex flex-wrap gap-2">{r.files.map(p => <HandoverPhoto key={p} handoverId={h.id} attachmentId={p} small={small} />)}</div>
                    : <>{r.flag && <span aria-hidden className="mr-1.5 inline-block h-1.5 w-1.5 -translate-y-px rounded-full bg-amber-500 align-middle" />}{r.value}{r.flag && <span className="sr-only"> (worth a look)</span>}</>}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}

/** Read-only body of a handover (detail, the pending prompt): its answers; the template it used in a muted line. */
export function HandoverBody({ h, small }: { h: Handover; small?: boolean }) {
  return <HandoverAnswers h={h} small={small} />;
}

/** "Closing checks template, version 3": for the detail view only. */
export function HandoverTemplateLine({ h }: { h: Handover }) {
  if (!h.form_name) return null;
  return <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="handover-template">Template: {h.form_name}, version {h.form_version}{h.form_version_title ? ` (“${h.form_version_title}”)` : ''}</p>;
}

/**
 * The entry header: the author opens their person sheet; building, team, position and time are muted. `time` = clock only
 * (under a day heading). `titled` (t279u) puts the template's name above it as the entry's title (the list, the clock-in
 * prompt); the detail view has it as the modal title instead.
 */
export function HandoverMeta({ h, time, onOpen, titled }: { h: Handover; time?: boolean; onOpen?: () => void; titled?: boolean }) {
  if (titled) return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100" data-testid="handover-title">{handoverTitle(h)}</h3>
      <HandoverMeta h={h} time={time} onOpen={onOpen} />
    </div>
  );
  const when = dt(h.created_at).toFormat(time ? DATE_FORMATS.time : DATE_FORMATS.weekdayTime);
  const where = [h.location_name, h.team_name, h.position_name].filter(Boolean).join(' · ');
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 text-sm text-slate-500 dark:text-slate-400" data-testid="handover-meta">
      <PersonButton userId={h.author_id} name={h.author_name} className="rounded text-left font-medium text-slate-700 hover:underline focus-visible:outline-2 dark:text-slate-200">{h.author_name}</PersonButton>
      {where && <span>· {where}</span>}
      <span>·</span>
      {onOpen
        ? <button type="button" onClick={e => { e.stopPropagation(); onOpen(); }} title={fullDateTime(h.created_at)} aria-label={`Open ${handoverTitle(h)} from ${h.author_name}, ${fullDateTime(h.created_at)}`}
            className="rounded hover:text-slate-900 hover:underline focus-visible:outline-2 dark:hover:text-white" data-testid="handover-open">{when}</button>
        : <time dateTime={h.created_at} title={fullDateTime(h.created_at)}>{when}</time>}
      {h.priority === 'important' && <span className="font-semibold text-rose-700 dark:text-rose-300" data-testid="handover-important">· Important</span>}
    </div>
  );
}

/** Pending handovers for my shift, one at a time, each acknowledged ("Got it"). Opened after clock-in and from banners. */
export function PendingHandoversModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const pending = useQuery({ queryKey: [...handoverKeys(ws ?? ''), 'pending'], queryFn: () => api.wf.pendingHandovers(ws!), enabled: Boolean(ws && open) });
  const list = pending.data ?? [];
  const cur = list[0];
  const ack = useMutation({
    mutationFn: (id: string) => api.wf.ackHandover(ws!, id),
    onSuccess: () => { invalidate(ws); if (list.length <= 1) { toast('Handovers acknowledged', 'success'); onClose(); } },
    onError: e => toast(errorMessage(e), 'error'),
  });
  return (
    <Modal open={open} onClose={onClose} title={<span className="flex items-center gap-2"><ClipboardSignature className="h-5 w-5 text-accent-fg" />Handover from the last shift{list.length > 1 ? ` (1 of ${list.length})` : ''}</span>}
      footer={cur ? <><Button variant="ghost" onClick={onClose}>Later</Button><Button onClick={() => ack.mutate(cur.id)} loading={ack.isPending}><CheckCircle2 className="h-4 w-4" />Got it</Button></> : <Button onClick={onClose}>Close</Button>}>
      {pending.isLoading ? <Spinner /> : cur ? <div className="space-y-3"><HandoverMeta h={cur} titled /><HandoverBody h={cur} /></div> : <p className="text-sm text-slate-500">You're up to date - no handovers waiting.</p>}
    </Modal>
  );
}

/** Banner for My shifts and home: "N handovers waiting for you". */
export function PendingHandoversBanner() {
  const ws = useWorkspaceId();
  const [open, setOpen] = useState(false);
  const pending = useQuery({ queryKey: [...handoverKeys(ws ?? ''), 'pending'], queryFn: () => api.wf.pendingHandovers(ws!), enabled: Boolean(ws), refetchInterval: 120_000 });
  const n = pending.data?.length ?? 0;
  if (!n) return null;
  const important = pending.data!.some(h => h.priority === 'important');
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="handover-banner"
        className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition hover:shadow-sm ${important ? 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-100' : 'border-accent-line bg-accent/10 text-ink dark:text-slate-100'}`}>
        <ClipboardSignature className="h-5 w-5 shrink-0" />
        <span className="flex-1"><strong>{n} handover{n === 1 ? '' : 's'}</strong> from the last shift {n === 1 ? 'is' : 'are'} waiting for you{important ? ' - including an important one' : ''}.</span>
        <span className="font-semibold underline">Read</span>
      </button>
      <PendingHandoversModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** t692u: "No handover templates yet." with a Create template link for people who can manage handover templates. */
export function NoHandoverTemplates({ className }: { className?: string }) {
  const canTemplates = useCanAnywhere('workforce.handover.templates');
  return (
    <p className={clsx('text-sm text-slate-500 dark:text-slate-400', className)} data-testid="handover-no-templates">
      No handover templates yet.{canTemplates && <> <Link to={handoverTemplateLink('new')} className="font-medium text-accent-fg hover:underline">Create template</Link></>}
    </p>
  );
}

/** Write (or, within 12h, edit) a handover. Location defaults to where you are clocked in / your shift. */
export function HandoverFormModal({ open, onClose, defaults, editing }: {
  open: boolean; /** saved = true after a successful save (else cancelled). */ onClose: (saved?: boolean) => void; defaults?: Partial<HandoverInput>; editing?: Handover | null;
}) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const { locations, positions } = useWfLookups();
  const { teams } = useTeams();
  const { userId } = useAuth();
  const [form, setForm] = useState<HandoverInput>({ location_id: '' });
  const [photos, setPhotos] = useState<{ id: string; name: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  // #34 templates: the chosen template ('' = the most specific one that applies) and its answers.
  const [pick, setPick] = useState('');
  const [answers, setAnswers] = useState<FormValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fileNames, setFileNames] = useState<Record<string, string>>({});
  // Reset only when the modal opens (or switches record); keyed on primitives so typing is never wiped.
  const defLoc = defaults?.location_id ?? locations[0]?.id ?? '';
  const defShift = defaults?.shift_id ?? null;
  useEffect(() => {
    if (!open) return;
    setPick(''); setErrors({});
    if (editing) {
      setForm({ location_id: editing.location_id, priority: editing.priority, ...Object.fromEntries(SECTIONS.map(s => [s.key, editing[s.key] ?? ''])) });
      const files = new Set((editing.form_sections ?? []).flatMap(s => s.fields).filter(f => f.type === 'file').flatMap(f => (editing.answers?.[f.id] as string[] | undefined) ?? []));
      setPhotos(editing.photo_ids.filter(id => !files.has(id)).map((id, i) => ({ id, name: `Photo ${i + 1}` })));
      setAnswers(editing.answers ?? {});
    } else {
      setForm({ location_id: defLoc, shift_id: defShift, priority: 'normal' });
      setPhotos([]);
    }
  }, [open, editing, defLoc, defShift]);
  // Department teams I belong to (the API also allows managers to target any team they oversee).
  const resolved = useQuery({
    queryKey: ['scoped-forms', ws ?? '', 'handover', 'resolve', form.location_id, form.team_id ?? '', form.position_id ?? ''],
    queryFn: () => api.resolveScopedForms(ws!, 'handover', { location_id: form.location_id, team_id: form.team_id ?? null, position_id: form.position_id ?? null }),
    enabled: Boolean(ws && open && !editing && form.location_id),
  });
  const options = resolved.data?.options ?? [];
  const chosen = editing ? null : options.find(o => o.id === pick) ?? resolved.data?.applied ?? null;
  const sections: FormSection[] | null = editing ? editing.form_sections : chosen?.sections ?? null;
  // Pre-filled values when the template (not the typing) changes; keyed on the id so answers are never wiped mid-edit.
  const chosenId = chosen?.id ?? '';
  const chosenVersion = chosen?.version ?? 0;
  useEffect(() => { if (!editing && chosen) { setAnswers(sectionDefaults(chosen.sections)); setErrors({}); } }, [chosenId, chosenVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const myTeams = teams.filter(t => t.kind === 'department' && (!t.members || t.members.some(m => m.user_id === userId)));
  const set = (k: keyof HandoverInput, v: string | null) => setForm(f => ({ ...f, [k]: v }));
  const save = useMutation({
    mutationFn: () => {
      if (sections) {
        const v = validateSectionAnswers(sections, answers);
        setErrors(v.errors);
        if (Object.keys(v.errors).length) throw new Error('Check the highlighted fields');
        if (editing) return api.wf.updateHandover(ws!, editing.id, { answers: v.values, photo_ids: photos.map(p => p.id), priority: form.priority });
        return api.wf.createHandover(ws!, { location_id: form.location_id, team_id: form.team_id || null, position_id: form.position_id || null, shift_id: form.shift_id ?? null,
          priority: form.priority, photo_ids: photos.map(p => p.id), form_id: chosen!.id, answers: v.values });
      }
      const body = { ...form, photo_ids: photos.map(p => p.id) };
      for (const s of SECTIONS) if (!body[s.key]?.trim()) body[s.key] = null;
      if (!editing) throw new Error('No handover templates yet');
      const { location_id: _l, team_id: _t, position_id: _p, shift_id: _s, ...rest } = body; void _l; void _t; void _p; void _s;
      return api.wf.updateHandover(ws!, editing.id, rest);
    },
    onSuccess: () => { invalidate(ws); toast(editing ? 'Handover updated' : 'Handover saved - the next shift will see it when they clock in', 'success'); onClose(true); },
    onError: e => toast(errorMessage(e), 'error'),
  });
  const addPhotos = async (files: FileList | null) => {
    if (!files?.length || !ws) return;
    setUploading(true);
    try {
      for (const f of Array.from(files).slice(0, 10 - photos.length)) {
        const a = await uploadFile(f, { workspace_id: ws, purpose: 'handover' });
        setPhotos(p => [...p, { id: a.id, name: f.name }]);
      }
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setUploading(false); }
  };
  // t692u: a new handover needs a template; only an older handover written on the retired built-in form edits its text sections.
  const empty = sections ? false : !editing || (SECTIONS.every(s => !form[s.key]?.trim()) && !photos.length);
  const upload = async (file: File) => { const a = await uploadFile(file, { workspace_id: ws!, purpose: 'handover' }); return { id: a.id, file_name: file.name }; };
  return (
    <Modal open={open} onClose={() => onClose()} wide title={editing ? 'Edit handover' : 'Write a handover'}
      footer={<DialogFooter onCancel={() => onClose()} onSave={() => save.mutate()} saving={save.isPending} disabled={empty || uploading || !form.location_id} saveLabel={editing ? 'Save changes' : 'Save handover'} />}>
      <div className="space-y-4">
        {!editing && (
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Building"><Select value={form.location_id} onChange={e => set('location_id', e.target.value)}>{locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</Select></Field>
            <Field label="For team" hint="optional"><Select value={form.team_id ?? ''} onChange={e => set('team_id', e.target.value || null)}><option value="">Everyone on the next shift</option>{myTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</Select></Field>
            <Field label="For position" hint="optional"><Select value={form.position_id ?? ''} onChange={e => set('position_id', e.target.value || null)}><option value="">Any position</option>{positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</Select></Field>
          </div>
        )}
        {!editing && options.length > 0 && (
          <Field label="Template" hint={options.length > 1 ? 'the most specific one is picked for you' : undefined}>
            <Select aria-label="Handover template" value={chosen?.id ?? ''} onChange={e => setPick(e.target.value)}>
              {options.map(o => <option key={o.id} value={o.id}>{o.name} ({FORM_SCOPE_LABELS[o.scope_type]}{o.scope_name ? `: ${o.scope_name}` : ''})</option>)}
            </Select>
          </Field>
        )}
        {!editing && !sections ? (
          resolved.isLoading || !form.location_id ? <Spinner /> : <NoHandoverTemplates />
        ) : sections ? (
          <SectionedFormInput sections={sections} values={answers} onChange={setAnswers} errors={errors} upload={upload} labels={fileNames} onLabel={(id, n) => setFileNames(x => ({ ...x, [id]: n }))} />
        ) : SECTIONS.map(s => (
          <Field key={s.key} label={s.label} hint={s.hint}>
            <Textarea rows={2} maxLength={4000} value={form[s.key as SectionKey] ?? ''} onChange={e => set(s.key, e.target.value)} />
          </Field>
        ))}
        {(editing || sections) && <div className="flex flex-wrap items-center gap-2">
          {photos.map(p => (
            <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs dark:bg-slate-800">
              <ImageIcon className="h-3 w-3" />{p.name}
              <button type="button" aria-label={`Remove ${p.name}`} onClick={() => { setPhotos(ps => ps.filter(x => x.id !== p.id)); discardUpload(p.id); }}><X className="h-3 w-3" /></button>
            </span>
          ))}
          {photos.length < 10 && !sections?.some(x => x.fields.some(f => f.type === 'file')) && (
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg px-2 py-1 text-sm text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50 dark:text-slate-300 dark:ring-slate-700">
              <Camera className="h-4 w-4" />{uploading ? 'Uploading…' : 'Add photos'}
              <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={e => void addPhotos(e.target.files)} />
            </label>
          )}
          <label className="ml-auto flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.priority === 'important'} onChange={e => set('priority', e.target.checked ? 'important' : 'normal')} />
            Important - notify the next shift now
          </label>
        </div>}
      </div>
    </Modal>
  );
}

/** Detail with acknowledgements (author and managers see who has read it). */
export function HandoverDetailModal({ id, onClose, onEdit }: { id: string | null; onClose: () => void; onEdit?: (h: HandoverDetail) => void }) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const q = useQuery({ queryKey: [...handoverKeys(ws ?? ''), 'one', id], queryFn: () => api.wf.handover(ws!, id!), enabled: Boolean(ws && id) });
  const h = q.data;
  const editable = h && h.author_id === userId && Date.now() - Date.parse(h.created_at) < 12 * 3600_000;
  return (
    <Modal open={Boolean(id)} onClose={onClose} wide title={h ? handoverTitle(h) : 'Handover'} footer={<>{editable && onEdit && <Button variant="secondary" onClick={() => onEdit(h)}>Edit</Button>}<Button onClick={onClose}>Close</Button></>}>
      {!h ? <Spinner /> : (
        <div className="space-y-4">
          <HandoverMeta h={h} />
          <HandoverBody h={h} />
          <HandoverTemplateLine h={h} />
          <div className="border-t border-slate-200 pt-3 dark:border-slate-800">
            <h3 className="mb-1 text-sm font-semibold text-slate-900 dark:text-slate-100">Acknowledged <span className="font-normal text-slate-500">{h.ack_count}</span></h3>
            {h.acks.length ? (
              <ul className="space-y-1 text-sm">{h.acks.map(a => <li key={a.user_id} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-700" /><PersonButton userId={a.user_id} name={a.display_name} className="rounded text-left hover:underline focus-visible:outline-2">{a.display_name}</PersonButton><span className="text-slate-500">· <RelativeTime iso={a.acked_at} /></span></li>)}</ul>
            ) : <p className="text-sm text-slate-500">Nobody yet.</p>}
          </div>
        </div>
      )}
    </Modal>
  );
}
